import { ReactElement } from 'react';
import { SubscriptionStatus } from '@sc/graphql';
import { Button, Input, LinearLoader } from '@sc/shared-ui';
import { useMutation } from '@apollo/client/react';
import { useForm } from '@tanstack/react-form';
import { useUser } from '../providers/users';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';
import {
  GQL_MUTATION_UPDATE_BILLING_DETAILS,
  GQL_MUTATION_UPDATE_COMPANY,
  GQL_QUERY_LIST_PAYMENT_METHODS,
} from '../utility/queries';
import { useAppForm } from '../forms/appForm';
import {
  BillingDetailsFields,
  toBillingDetailsInput,
} from '../components/BillingDetailsFields';

export const SettingsCompanyTab = (): ReactElement => {
  const { user } = useUser();
  const { useApolloError, setAlertModal } = useAlerts();
  const onError = useApolloError();

  const [updateCompany, { loading: isLoading }] = useMutation(
    GQL_MUTATION_UPDATE_COMPANY
  );

  const [updateBillingDetails, { loading: isBillingLoading }] = useMutation(
    GQL_MUTATION_UPDATE_BILLING_DETAILS,
    { refetchQueries: [GQL_QUERY_LIST_PAYMENT_METHODS] }
  );

  const form = useForm({
    defaultValues: {
      name: user.company.name ?? '',
    },
    onSubmit: ({ value }) => {
      void updateCompany({
        variables: {
          input: { name: value.name },
        },
        onCompleted: () => {
          track('Company Details Saved');

          notify.success('Company updated');
        },
        onError,
      });
    },
  });

  const billingForm = useAppForm({
    defaultValues: {
      billing: {
        country: user.company.billingCountry ?? '',
        vatNumber: user.company.vatNumber ?? '',
        addressLine1: user.company.billingAddressLine1 ?? '',
        addressLine2: user.company.billingAddressLine2 ?? '',
        city: user.company.billingCity ?? '',
        postcode: user.company.billingPostalCode ?? '',
      },
    },
    onSubmit: async ({ value }) => {
      await updateBillingDetails({
        variables: { input: toBillingDetailsInput(value.billing) },
        onCompleted: (data) => {
          const { removedPaymentMethods, company } = data.updateBillingDetails;

          track('Billing Address Saved', { removedPaymentMethods });

          if (removedPaymentMethods > 0) {
            const paused =
              company.subscription?.status === SubscriptionStatus.paused;

            setAlertModal({
              title: 'Payment methods changed',
              description: `Your billing details were saved. They change the VAT treatment of ${
                removedPaymentMethods === 1
                  ? 'one of your saved cards, so it has'
                  : 'your saved cards, so they have'
              } been removed — re-add a card to keep payments running at the correct rate.${
                paused
                  ? ' Your subscription has been paused until you do.'
                  : ''
              }`,
              icon: 'warning',
              open: true,
            });
          } else {
            notify.success('Billing details updated');
          }
        },
        onError,
      });
    },
  });

  return (
    <>
      {(isLoading || isBillingLoading) && <LinearLoader />}
      <form
        className="p-5 max-w-md space-y-5"
        onSubmit={(e) => {
          e.preventDefault();

          e.stopPropagation();

          void form.handleSubmit();
        }}
      >
        <form.Field
          name="name"
          children={({ state, handleChange }) => (
            <Input
              label="Company name"
              value={state.value}
              required
              onChange={(e) => handleChange(e.target.value)}
            />
          )}
        />
        <div className="flex justify-end">
          <Button
            button={{
              text: 'Save changes',
              type: 'primary',
              size: 'medium',
              shrink: true,
              isSubmit: true,
              disabled: isLoading,
            }}
          />
        </div>
      </form>
      <hr className="border-none shrink-0 h-px w-full bg-grey-700" />
      <form
        className="p-5 max-w-md space-y-5"
        onSubmit={(e) => {
          e.preventDefault();

          e.stopPropagation();

          void billingForm.handleSubmit();
        }}
      >
        <div className="space-y-1">
          <h3 className="font-grotesque text-h3 font-semibold">
            Billing address
          </h3>
          <p className="text-sm text-text-low-priority">
            Used for invoices and to work out the VAT on your payments. Saved
            cards whose VAT treatment changes are removed and need re-adding.
          </p>
        </div>
        <BillingDetailsFields form={billingForm} fields="billing" />
        <div className="flex justify-end">
          <Button
            button={{
              text: 'Save changes',
              type: 'primary',
              size: 'medium',
              shrink: true,
              isSubmit: true,
              disabled: isBillingLoading,
            }}
          />
        </div>
      </form>
    </>
  );
};
